import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Typography } from 'antd';
import { SbButton, SbInput, SbTag, SbTooltip, SbNotification } from 'uiCore';
import SbUpload from 'uiCore/SbUpload/SbUpload';
import SbDivider from 'uiCore/SbDivider/SbDivider';
import ThumbIconUp from 'Components/SBLayoutIcon/ThumbIconUp';
import ThumbIconDown from 'Components/SBLayoutIcon/ThumbIconDown';
import FileIcon from 'Components/SBLayoutIcon/FileIcon';
import FileuploadIcon from 'Components/SBLayoutIcon/FileuploadIcon';
import { SB_COLORS } from 'theme/SbThemeV2';
import { FEEDBACK_TYPE } from 'constants/jobs.constants';
import {
    FeedbackCardStyled,
    RatingBtnStyled,
    SubmitBtnStyled,
    TooltipAnchorStyled,
    UploadBtnStyled,
} from './FeedbackV2.styles';

const { Text, Title } = Typography;

const MAX_FILES = 5;

const TITLE = 'Share any issues you encounter and get notified when they\'re resolved.';
const ACTION_LABEL = 'Submit';
const DOWN_PLACEHOLDER = 'Example:\n1. Format is not correct\n2. Edit button is not working\n3. Cannot download the JD\n...\n';

const getPromptText = (rating) => {
    if (rating === 'UP') return 'What worked well? Share details so we can keep it consistent.';
    if (rating === 'DOWN') return 'What went wrong? Share details so we can fix it.';
    return 'Choose one option to start.';
};

const getPlaceholderText = (rating) => {
    if (rating === 'UP') return 'Example:\n1. The output format is clear\n2. The flow is easy\n3. Fast and smooth\n...\n';
    if (rating === 'DOWN') return DOWN_PLACEHOLDER;
    return 'Select an option below to continue';
};

const FeedbackV2 = ({
    onClose,
    actionFunction,
    value,
    onChange,
    disabled,
    onFileUpload,
    loading,
    accept,              // optional: restrict the file picker (e.g. "image/*,video/*")
    allowedTypes,        // optional: array of exact MIME types to allow
    maxSizeMB,           // optional: per-file size cap in MB
}) => {
    const [files, setFiles] = useState([]);
    const [rating, setRating] = useState(null);

    const toggleRating = (next) => {
        if (disabled) return;
        setRating(prev => (prev === next ? null : next));
    };

    const removeFile = (idx) => {
        const updated = files.filter((_, i) => i !== idx);
        setFiles(updated);
        onFileUpload?.(updated);
    };

    const handleCancel = () => {
        setFiles([]);
        setRating(null);
        onFileUpload?.([]);
        onClose?.();
    };

    const handleSubmit = () => {
        actionFunction?.({
            feedback_type: FEEDBACK_TYPE[rating] ?? null,
            comment: value,
            files,
        });
    };

    const beforeUpload = (file) => {
        // Optional client-side validation (only enforced when the props are set).
        if (Array.isArray(allowedTypes) && allowedTypes.length && !allowedTypes.includes(file.type)) {
            SbNotification?.error?.('Invalid File Type', 'That file type is not supported.');
            return false;
        }
        if (maxSizeMB && file.size / 1024 / 1024 > maxSizeMB) {
            SbNotification?.error?.('File Too Large', `Files must be smaller than ${maxSizeMB}MB.`);
            return false;
        }
        setFiles(prev => {
            const combined = [...prev, file]
                .filter((f, i, arr) => arr.findIndex(x => x.name === f.name && x.size === f.size) === i)
                .slice(0, MAX_FILES);
            onFileUpload?.(combined);
            return combined;
        });
        return false;
    };

    const canSubmit = useMemo(
        () => !disabled && !loading && !!rating && typeof value === 'string' && value.trim().length > 0,
        [disabled, loading, rating, value]
    );

    const uploadDisabled = !rating || disabled || loading || files.length >= MAX_FILES;

    return (
        <FeedbackCardStyled role="dialog" aria-label={TITLE}>
            <Flex vertical gap={12}>
                <Title level={5}>{TITLE}</Title>
                <Text type="secondary">{getPromptText(rating)}</Text>

                <SbInput
                    textarea
                    value={value}
                    placeholder={getPlaceholderText(rating)}
                    rows={6}
                    onChange={onChange}
                    disabled={disabled || !rating || loading}
                />

                {files.length > 0 && (
                    <Flex gap={8} wrap="wrap">
                        {files.map((file, idx) => (
                            <SbTooltip
                                key={`${file.name}-${idx}`}
                                title={file.name}
                                placement="top"
                                getPopupContainer={() => document.body}
                            >
                                <TooltipAnchorStyled>
                                    <SbTag
                                        icon={<FileIcon color="#8F95B2" />}
                                        closable={!disabled && !loading}
                                        onClose={() => removeFile(idx)}
                                    />
                                </TooltipAnchorStyled>
                            </SbTooltip>
                        ))}
                    </Flex>
                )}

                {accept && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Tip: pick an option above, then use “Attach” to add a screenshot or short screen recording (optional).
                    </Text>
                )}

                <Flex vertical gap={8}>
                    <Flex align="center" gap={8}>
                        <RatingBtnStyled
                            $active={rating === 'UP' ? 'up' : undefined}
                            onClick={() => toggleRating('UP')}
                            disabled={disabled || loading}
                            aria-pressed={rating === 'UP'}
                            icon={<ThumbIconUp color={rating === 'UP' ? SB_COLORS.primary : '#8F95B2'} />}
                        >
                            Works as expected
                        </RatingBtnStyled>

                        <RatingBtnStyled
                            $active={rating === 'DOWN' ? 'down' : undefined}
                            onClick={() => toggleRating('DOWN')}
                            disabled={disabled || loading}
                            aria-pressed={rating === 'DOWN'}
                            icon={<ThumbIconDown color={rating === 'DOWN' ? '#dc2626' : '#8F95B2'} />}
                        >
                            Has issues
                        </RatingBtnStyled>
                    </Flex>

                    <Flex align="center" justify="space-between" gap={8}>
                        <SbButton onClick={handleCancel} disabled={disabled || loading}>
                            Cancel
                        </SbButton>
                        <Flex align="center" gap={8}>
                            <SbTooltip
                                title={!rating ? 'Select an option first' : `Upload file (${MAX_FILES - files.length} left)`}
                                placement="top"
                                getPopupContainer={() => document.body}
                            >
                                <TooltipAnchorStyled>
                                    <SbUpload
                                        beforeUpload={beforeUpload}
                                        showUploadList={false}
                                        fileList={[]}
                                        multiple
                                        accept={accept}
                                        disabled={uploadDisabled}
                                    >
                                        <UploadBtnStyled
                                            buttonType="primary"
                                            icon={<FileuploadIcon color="#fff" />}
                                            disabled={uploadDisabled}
                                        >
                                            Attach
                                        </UploadBtnStyled>
                                    </SbUpload>
                                </TooltipAnchorStyled>
                            </SbTooltip>

                            <SubmitBtnStyled
                                buttonType="primary"
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                                loading={loading}
                            >
                                {loading ? 'Submit' : ACTION_LABEL}
                            </SubmitBtnStyled>
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>
        </FeedbackCardStyled>
    );
};

export default FeedbackV2;
